import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  Typography,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  Badge,
  Avatar,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Mic as MicIcon,
  PhotoCamera as PhotoCameraIcon,
  History as HistoryIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSearch } from '../../context/SearchContext';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import useSearchOptimization from '../../hooks/useSearchOptimization';
import SearchAnalyticsDashboard from './SearchAnalyticsDashboard';

const IntelligentSearchBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const {
    query,
    suggestions,
    isLoading,
    updateQuery,
    forceSearch,
    getSuggestions,
    searchByCategory,
    searchBySavedLocation,
    toggleFilterPanel,
    isFilterPanelOpen,
    filters,
    updateFilters
  } = useSearch();

  // Hook d'optimisation pour les nouvelles fonctionnalités
  const {
    getPersonalizedSuggestions,
    trackInteraction,
    performOptimizedSearch,
    hasPersonalization
  } = useSearchOptimization();

  // États locaux
  const [localQuery, setLocalQuery] = useState(query);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isRecording, setIsRecording] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [showAnalyticsDashboard, setShowAnalyticsDashboard] = useState(false);

  // Refs
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const recognitionRef = useRef(null);

  // Debounced functions
  const debouncedGetSuggestions = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length >= 2) {
        // Obtenir les suggestions standards
        getSuggestions(searchQuery);
        
        // Obtenir aussi les suggestions personnalisées si disponible
        if (hasPersonalization && user?.id) {
          try {
            const personalizedSuggestions = await getPersonalizedSuggestions(searchQuery);
            if (personalizedSuggestions.length > 0) {
              // Combiner avec les suggestions standards
              // Cette logique pourrait être améliorée dans le contexte SearchContext
              console.log('[IntelligentSearchBar] Got personalized suggestions:', personalizedSuggestions);
            }
          } catch (error) {
            console.error('[IntelligentSearchBar] Error getting personalized suggestions:', error);
          }
        }
      }
    }, 300),
    [getSuggestions, getPersonalizedSuggestions, hasPersonalization, user?.id]
  );

  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.trim()) {
        try {
          // Tracker l'interaction de recherche
          await trackInteraction({
            type: 'search_initiated',
            query: searchQuery,
            filters: filters,
            source: 'search_bar'
          });
          
          // Effectuer la recherche optimisée
          await performOptimizedSearch(searchQuery, filters);
        } catch (error) {
          console.error('[IntelligentSearchBar] Error in optimized search:', error);
          // Fallback vers la recherche standard avec query explicite
          forceSearch(searchQuery, filters);
        }
      }
    }, 500),
    [forceSearch, performOptimizedSearch, trackInteraction, filters]
  );

  // Effets
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  useEffect(() => {
    if (localQuery !== query) {
      updateQuery(localQuery);
      debouncedGetSuggestions(localQuery);
    }
  }, [localQuery, query, updateQuery, debouncedGetSuggestions]);

  // Initialiser la reconnaissance vocale
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = user?.preferences?.language === 'ar' ? 'ar-DZ' : 
                                   user?.preferences?.language === 'en' ? 'en-US' : 'fr-FR';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setIsRecording(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setLocalQuery(transcript);
        setShowSuggestions(false);
        setTimeout(() => forceSearch(transcript, filters), 100);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        setIsRecording(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setIsRecording(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [user?.preferences?.language, forceSearch]);

  // Charger les recherches récentes et tendances
  useEffect(() => {
    // Charger depuis localStorage
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(recent.slice(0, 5));

    // Charger les tendances (simulation)
    setTrendingSearches([
      { text: 'Perceuse électrique', icon: 'trending', count: 156 },
      { text: 'Échelle télescopique', icon: 'trending', count: 89 },
      { text: 'Tondeuse gazon', icon: 'trending', count: 67 },
      { text: 'Scie circulaire', icon: 'trending', count: 45 }
    ]);
  }, []);

  // Gestionnaires d'événements
  const handleInputChange = (event) => {
    const value = event.target.value;
    setLocalQuery(value);
    setSelectedSuggestionIndex(-1);
    
    if (value.length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    if (localQuery.length >= 2 || suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Délai pour permettre le clic sur les suggestions
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleKeyDown = (event) => {
    // Gérer la touche Entrée même sans suggestions
    if (event.key === 'Enter') {
      event.preventDefault();
      if (showSuggestions && selectedSuggestionIndex >= 0) {
        handleSuggestionSelect(getAllSuggestions()[selectedSuggestionIndex]);
      } else {
        handleSearch();
      }
      return;
    }

    // Le reste de la logique ne fonctionne que si les suggestions sont affichées
    if (!showSuggestions) return;

    const suggestionCount = suggestions.length + recentSearches.length + trendingSearches.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestionCount - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedSuggestionIndex(prev => prev > -1 ? prev - 1 : prev);
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleSearch = async () => {
    if (localQuery.trim()) {
      try {
        // Ajouter à l'historique
        addToRecentSearches(localQuery);
        setShowSuggestions(false);
        
        // Tracker l'interaction de recherche
        await trackInteraction({
          type: 'search_executed',
          query: localQuery,
          filters: filters,
          source: 'search_bar',
          method: 'enter_key'
        });
        
        // Mettre à jour la query dans le contexte
        updateQuery(localQuery);
        
        // Effectuer la recherche avec la query explicite pour éviter le stale state en prod
        forceSearch(localQuery, filters);
      } catch (error) {
        console.error('[IntelligentSearchBar] Error in search execution:', error);
        // Fallback vers la recherche standard
        updateQuery(localQuery);
        forceSearch(localQuery, filters);
      }
    }
  };

  const handleClear = () => {
    setLocalQuery('');
    updateQuery('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleVoiceSearch = () => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start();
    } else if (isListening) {
      recognitionRef.current.stop();
    }
  };

  const handleImageSearch = () => {
    // TODO: Implémenter la recherche par image
    console.log('Image search not implemented yet');
  };

  // 🧹 RÉINITIALISER LA PAGE DE RECHERCHE (comme le bouton effacer du sidebar)
  const handleResetSearch = useCallback(() => {
    // Réinitialiser la query locale et globale
    setLocalQuery('');
    updateQuery('');
    setShowSuggestions(false);
    
    // Réinitialiser tous les filtres (même logique que handleClearFilters du sidebar)
    const clearedFilters = {
      ...filters,
      country: '',
      cities: [],
      categoryId: null,
      minPrice: null,
      maxPrice: null,
      _timestamp: Date.now(),
      _triggerSearch: true,
      _activeCount: 0
    };

    updateFilters(clearedFilters);
    
    // Remettre le focus sur le champ de recherche
    inputRef.current?.focus();
    
    console.log('[IntelligentSearchBar] Search page reset - all filters and query cleared');
  }, [updateQuery, updateFilters, filters]);

  const handleSuggestionSelect = async (suggestion) => {
    try {
      console.log('[IntelligentSearchBar] Suggestion selected:', suggestion);
      
      // Tracker l'interaction de sélection de suggestion
      await trackInteraction({
        type: 'suggestion_selected',
        suggestionType: suggestion.type || 'unknown',
        suggestionText: suggestion.text,
        source: 'search_bar'
      });

      switch (suggestion.type) {
        case 'listing':
        case 'history':
        case 'suggestion':
        case 'synonym':
        case 'tool_type':
          setLocalQuery(suggestion.text);
          updateQuery(suggestion.text);
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          
          // Attendre un peu puis effectuer la recherche
          setTimeout(() => {
            console.log('[IntelligentSearchBar] Performing search for:', suggestion.text);
            forceSearch(suggestion.text, filters);
          }, 100);
          break;
          
        case 'category':
          await trackInteraction({
            type: 'category_selected',
            categoryId: suggestion.categoryId,
            source: 'search_bar'
          });
          setShowSuggestions(false);
          searchByCategory(suggestion.categoryId);
          break;
          
        case 'location':
          await trackInteraction({
            type: 'location_selected',
            location: suggestion.text,
            source: 'search_bar'
          });
          setShowSuggestions(false);
          searchBySavedLocation(suggestion.text);
          break;
          
        case 'trending':
          setLocalQuery(suggestion.text);
          updateQuery(suggestion.text);
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          
          setTimeout(() => {
            console.log('[IntelligentSearchBar] Performing trending search for:', suggestion.text);
            forceSearch(suggestion.text, filters);
          }, 100);
          break;
          
        default:
          // Par défaut, traiter comme une recherche textuelle
          setLocalQuery(suggestion.text);
          updateQuery(suggestion.text);
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          
          setTimeout(() => {
            console.log('[IntelligentSearchBar] Performing default search for:', suggestion.text);
            forceSearch(suggestion.text, filters);
          }, 100);
          break;
      }
    } catch (error) {
      console.error('[IntelligentSearchBar] Error in suggestion selection:', error);
      // En cas d'erreur, faire une recherche simple
      setLocalQuery(suggestion.text);
      updateQuery(suggestion.text);
      setShowSuggestions(false);
      setTimeout(() => forceSearch(suggestion.text, filters), 100);
    }
    
    // Ajouter à l'historique
    addToRecentSearches(suggestion.text);
  };

  const addToRecentSearches = (searchText) => {
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const filtered = recent.filter(item => item !== searchText);
    const updated = [searchText, ...filtered].slice(0, 10);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    setRecentSearches(updated.slice(0, 5));
  };

  const clearRecentSearches = () => {
    localStorage.removeItem('recentSearches');
    setRecentSearches([]);
  };

  const getAllSuggestions = () => {
    const all = [];
    
    // Suggestions intelligentes
    all.push(...suggestions.map(s => ({ ...s, type: s.type || 'suggestion' })));
    
    // Recherches récentes
    if (recentSearches.length > 0 && localQuery.length < 2) {
      all.push(...recentSearches.map(text => ({ type: 'history', text, icon: 'history' })));
    }
    
    // Tendances
    if (trendingSearches.length > 0 && localQuery.length < 2) {
      all.push(...trendingSearches.map(trend => ({ 
        type: 'trending', 
        text: trend.text, 
        icon: 'trending',
        count: trend.count 
      })));
    }
    
    return all;
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => 
      value !== null && value !== '' && value !== false && 
      (Array.isArray(value) ? value.length > 0 : true)
    ).length;
  };

  const renderSuggestionIcon = (suggestion) => {
    const iconProps = { 
      sx: { 
        fontSize: 20,
        color: suggestion.type === 'trending' ? 'error.main' : 'text.secondary'
      }
    };

    switch (suggestion.icon) {
      case 'history':
        return <HistoryIcon {...iconProps} />;
      case 'category':
        return <CategoryIcon {...iconProps} />;
      case 'location':
        return <LocationIcon {...iconProps} />;
      case 'trending':
        return <TrendingUpIcon {...iconProps} />;
      default:
        return <SearchIcon {...iconProps} />;
    }
  };

  const allSuggestions = getAllSuggestions();

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Barre de recherche principale */}
      <TextField
        ref={inputRef}
        fullWidth
        value={localQuery}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={t('search.placeholder')}
        variant="outlined"
        size={isMobile ? 'medium' : 'large'}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            backgroundColor: '#ffffff !important', // Forcer le fond blanc
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: `2px solid ${theme.palette.primary.main}30`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 12px 40px rgba(63, 81, 181, 0.2)`,
              border: `2px solid ${theme.palette.primary.main}60`,
              backgroundColor: '#ffffff !important', // Garder le fond blanc au hover
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 4px ${theme.palette.primary.main}20`,
              border: `2px solid ${theme.palette.primary.main}`,
              backgroundColor: '#ffffff !important', // Garder le fond blanc au focus
            }
          },
          '& .MuiOutlinedInput-input': {
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: 500,
            padding: theme.spacing(isMobile ? 1.5 : 2, 1),
            color: theme.palette.text.primary,
            '&::placeholder': {
              color: `${theme.palette.primary.main}AA`,
              opacity: 1
            }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontSize: isMobile ? 24 : 28,
                  filter: 'drop-shadow(0 2px 4px rgba(63, 81, 181, 0.3))'
                }} 
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Boutons secondaires — masqués sur mobile pour agrandir la zone de saisie */}
                {!isMobile && (
                  <>
                    {/* Bouton de recherche vocale */}
                    <Tooltip title={t('search.voice.tooltip')}>
                      <IconButton
                        onClick={handleVoiceSearch}
                        disabled={!recognitionRef.current}
                        sx={{
                          color: isRecording ? theme.palette.secondary.main : theme.palette.primary.main + 'AA',
                          animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '10',
                            color: theme.palette.primary.main
                          },
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.1)' },
                            '100%': { transform: 'scale(1)' }
                          }
                        }}
                      >
                        <MicIcon />
                      </IconButton>
                    </Tooltip>

                    {/* Bouton de recherche par image */}
                    <Tooltip title={t('search.image.tooltip')}>
                      <IconButton
                        onClick={handleImageSearch}
                        sx={{ 
                          color: theme.palette.primary.main + 'AA',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '10',
                            color: theme.palette.primary.main
                          }
                        }}
                      >
                        <PhotoCameraIcon />
                      </IconButton>
                    </Tooltip>

                    {/* Bouton de réinitialisation de la recherche */}
                    <Tooltip title={t('search.reset.tooltip', 'Réinitialiser la recherche')}>
                      <IconButton
                        onClick={handleResetSearch}
                        sx={{
                          color: theme.palette.primary.main + 'AA',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '10',
                            color: theme.palette.primary.main,
                            transform: 'rotate(180deg)',
                            transition: 'all 0.3s ease'
                          }
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {/* Bouton Analytics Dashboard */}
                {user && (user.roles?.includes('admin') || user.roles?.includes('moderator')) && (
                  <Tooltip title={t('search.analytics.dashboard.tooltip')}>
                    <IconButton
                      onClick={() => setShowAnalyticsDashboard(true)}
                      sx={{
                        color: theme.palette.primary.main + 'AA',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.main + '10'
                        }
                      }}
                    >
                      <AnalyticsIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Bouton de nettoyage */}
                {localQuery && (
                  <Zoom in timeout={200}>
                    <IconButton
                      onClick={handleClear}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Zoom>
                )}

                {/* Bouton de recherche principal */}
                <Tooltip title={t('search.button.tooltip', 'Lancer la recherche')}>
                  <IconButton
                    onClick={handleSearch}
                    disabled={!localQuery.trim()}
                    sx={{
                      color: localQuery.trim() ? 'white' : theme.palette.primary.main + '60',
                      backgroundColor: localQuery.trim() ? theme.palette.primary.main : 'transparent',
                      border: `2px solid ${localQuery.trim() ? theme.palette.primary.main : theme.palette.primary.main + '40'}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: localQuery.trim() ? theme.palette.primary.dark : theme.palette.primary.main + '10',
                        color: localQuery.trim() ? 'white' : theme.palette.primary.main,
                        transform: 'scale(1.05)',
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
                      },
                      '&:disabled': {
                        color: theme.palette.primary.main + '40',
                        backgroundColor: 'transparent',
                        border: `2px solid ${theme.palette.primary.main}20`
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </InputAdornment>
          )
        }}
      />

      {/* Panneau de suggestions */}
      {showSuggestions && (
        <Fade in timeout={200}>
          <Paper
            ref={suggestionsRef}
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: theme.zIndex.modal,
              mt: 1,
              borderRadius: 2,
              overflow: 'hidden',
              maxHeight: 400,
              overflowY: 'auto',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
            }}
          >
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                {[...Array(3)].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width="60%" height={24} />
                  </Box>
                ))}
              </Box>
            ) : allSuggestions.length > 0 ? (
              <List sx={{ py: 0 }}>
                {/* Suggestions intelligentes */}
                {suggestions.length > 0 && (
                  <>
                    <ListItem sx={{ py: 1, px: 2, backgroundColor: 'action.hover' }}>
                      <ListItemIcon>
                        <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" fontWeight={600}>
                            {t('search.suggestions.intelligent')}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {suggestions.map((suggestion, index) => (
                      <ListItemButton
                        key={`suggestion-${index}`}
                        selected={selectedSuggestionIndex === index}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        sx={{
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.main + '15',
                            borderLeft: `3px solid ${theme.palette.primary.main}`
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '08'
                          }
                        }}
                      >
                        <ListItemIcon>
                          {renderSuggestionIcon(suggestion)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={suggestion.text}
                          secondary={suggestion.description}
                        />
                        {suggestion.count && (
                          <Chip 
                            label={suggestion.count} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </ListItemButton>
                    ))}
                    {(recentSearches.length > 0 || trendingSearches.length > 0) && (
                      <Divider />
                    )}
                  </>
                )}

                {/* Recherches récentes */}
                {recentSearches.length > 0 && localQuery.length < 2 && (
                  <>
                    <ListItem sx={{ py: 1, px: 2, backgroundColor: 'action.hover' }}>
                      <ListItemIcon>
                        <HistoryIcon sx={{ color: 'text.secondary' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" fontWeight={600}>
                            {t('search.suggestions.recent')}
                          </Typography>
                        }
                      />
                      <IconButton 
                        size="small" 
                        onClick={clearRecentSearches}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                    {recentSearches.map((searchText, index) => (
                      <ListItemButton
                        key={`recent-${index}`}
                        selected={selectedSuggestionIndex === suggestions.length + index}
                        onClick={() => handleSuggestionSelect({ type: 'history', text: searchText })}
                        sx={{
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main' + '10'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <HistoryIcon sx={{ color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText primary={searchText} />
                      </ListItemButton>
                    ))}
                    {trendingSearches.length > 0 && <Divider />}
                  </>
                )}

                {/* Tendances */}
                {trendingSearches.length > 0 && localQuery.length < 2 && (
                  <>
                    <ListItem sx={{ py: 1, px: 2, backgroundColor: 'action.hover' }}>
                      <ListItemIcon>
                        <TrendingUpIcon sx={{ color: 'error.main' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" fontWeight={600}>
                            {t('search.suggestions.trending')}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {trendingSearches.map((trend, index) => (
                      <ListItemButton
                        key={`trending-${index}`}
                        selected={selectedSuggestionIndex === suggestions.length + recentSearches.length + index}
                        onClick={() => handleSuggestionSelect({ type: 'trending', text: trend.text })}
                        sx={{
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main' + '10'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <TrendingUpIcon sx={{ color: 'error.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={trend.text} />
                        <Chip 
                          label={trend.count} 
                          size="small" 
                          color="error" 
                          variant="outlined"
                        />
                      </ListItemButton>
                    ))}
                  </>
                )}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('search.suggestions.empty')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Fade>
      )}

      {/* Dashboard Analytics */}
      <SearchAnalyticsDashboard
        open={showAnalyticsDashboard}
        onClose={() => setShowAnalyticsDashboard(false)}
      />
    </Box>
  );
};

export default IntelligentSearchBar;